// The right panel of the Studio Editor: where the photo goes in, is cropped, is edited and comes back — in the shape of the chosen
// print size — with the actions on the result. `StudioRecentPhotos` is the strip of recent studio photos under it (its own card, so the
// page can put it after the controls on a phone). Presentational: the page owns the state.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconDownload, IconEye, IconLoader2, IconMinus, IconPhotoUp, IconPlus, IconRuler2,
    IconThumbDown, IconThumbUp, IconUpload, IconX, IconZoomReset,
} from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import CompareSlider from '@/components/common/CompareSlider';
import PhotoEditDrawer from '@/components/common/PhotoEditDrawer';
import GeneratingOverlay from '@/components/common/GeneratingOverlay';
import { ActionBtn, EditToggle, PrintButton, btnIcon, customSizeIcon } from '@/components/common/PhotoActionButtons';
import RecentPhotosStrip from '@/components/common/RecentPhotosStrip';
import { useMediaQuery } from '../../../lib/hooks';
import { NEUTRAL_EDITS, isEdited } from '../../../utils/photoEdits';
import { numFor, sizeDims, sizeLabel } from './studioConstants';
import styles from './StudioPhotoArea.module.scss';

// The generating label cycles through these (ms from the start for steps 2…6), like the AI photo editor — no percentage.
const GEN_STEPS = ['genStep1', 'genStep2', 'genStep3', 'genStep4', 'genStep5', 'genStep6'];
const GEN_STEP_DELAYS = [3000, 6000, 10000, 14000, 18000];

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

/**
 * @param size            the studio size key — the frame takes its shape
 * @param previewUrl      the cropped photo (before generating / the "original" side of the compare)
 * @param resultUrl       the generated photo, or null
 * @param resultSize      the size key the result was made at (the panel's size may have changed since) — its view takes that shape
 * @param showResult      which side of the compare is shown
 * @param isGenerating    the busy state
 * @param onPreview       opens the result in the preview modal
 * @param edits / onEditsChange   the result's edits (utils/photoEdits.js) — shown live through `editedUrl`, never saved
 * @param editedUrl       the edited preview of the result (null while nothing is edited)
 * @param rated / feedbackLoading / onLike / onDislike   the like / dislike pills on the result (gone once rated)
 * @param filterThumbs / editCropped / onEditCrop / onClearEditCrop / onEditorOpen   the edit drawer's extras (see components/common/PhotoEditDrawer)
 */
export default function StudioPhotoArea({
    size, previewUrl, resultUrl, resultSize = null, showResult, onShowResult, isGenerating, edits = NEUTRAL_EDITS, onEditsChange, editedUrl = null,
    filterThumbs, editCropped = false, onEditCrop, onClearEditCrop, onEditorOpen,
    onPickFile, onRemove, onRecrop, onDownload, onCustomDownload, onPrint, onCopy, copying = false,
    rated = false, feedbackLoading = null, onLike, onDislike,
    isDragging, dragHandlers, inputRef, onSizeClick, onHide, onPreview,
}) {
    const { t, i18n } = useTranslation();
    const num = numFor(i18n.language);
    const shownUrl = resultUrl && showResult ? (editedUrl || resultUrl) : previewUrl;
    const dims = (resultUrl && showResult && sizeDims(resultSize)) || sizeDims(size) || { w: 2, h: 3 };
    const ratio = dims.w / dims.h;
    const hasPhoto = !!previewUrl;
    // A third view next to original / result: the before-after slider (counts as the result side for the actions below).
    const [compare, setCompare] = useState(false);
    useEffect(() => { if (!resultUrl) setCompare(false); }, [resultUrl]);
    const view = !resultUrl || !showResult ? 'original' : compare ? 'compare' : 'result';
    const pickView = (v) => { setCompare(v === 'compare'); onShowResult(v !== 'original'); };

    // Zoom (− / + / reset, Ctrl/⌘ + wheel) with drag-to-pan while zoomed — reset whenever another picture is shown.
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null);
    const stageRef = useRef(null);
    const frameRef = useRef(null);
    // When the height cap bites (a wide, short screen) a landscape frame is narrower than the card, leaving side gaps. No extra space is
    // added above / below (a short screen has no height to give away); the measured gap only places the floating buttons on the frame.
    const [sideGap, setSideGap] = useState(0);
    const landscape = ratio > 1;
    useLayoutEffect(() => {
        const stage = stageRef.current;
        const frame = frameRef.current;
        if (!landscape || !stage || !frame) { setSideGap(0); return undefined; }
        const measure = () => setSideGap(Math.max(0, Math.round((stage.clientWidth - frame.offsetWidth) / 2)));
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(stage);
        ro.observe(frame);
        return () => ro.disconnect();
    }, [landscape, ratio]);
    useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [shownUrl, view]);
    const setZoomTo = (z) => {
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 100) / 100));
        setZoom(next);
        if (next === 1) setPan({ x: 0, y: 0 });
    };
    const canZoom = hasPhoto && !isGenerating && view !== 'compare';
    useEffect(() => {
        const el = stageRef.current;
        if (!el || !canZoom) return undefined;
        const onWheel = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return; // a plain wheel still scrolls the page
            e.preventDefault();
            setZoom((z) => {
                const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((z + (e.deltaY < 0 ? 0.25 : -0.25)) * 100) / 100));
                if (next === 1) setPan({ x: 0, y: 0 });
                return next;
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [canZoom]);
    // While dragging the picture follows the pointer 1:1 — the zoom's ease-in transition is switched off, or it would trail behind.
    const [panning, setPanning] = useState(false);
    const endPan = () => { dragRef.current = null; setPanning(false); };
    const panHandlers = zoom > 1 ? {
        onPointerDown: (e) => { dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; setPanning(true); e.currentTarget.setPointerCapture?.(e.pointerId); },
        onPointerMove: (e) => { if (dragRef.current) setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y }); },
        onPointerUp: endPan,
        onPointerCancel: endPan,
    } : {};
    // A short un-blur when the result arrives.
    const [revealing, setRevealing] = useState(false);
    const wasGeneratingRef = useRef(false);
    useEffect(() => {
        const finished = wasGeneratingRef.current && !isGenerating;
        wasGeneratingRef.current = isGenerating;
        if (finished && resultUrl) setRevealing(true);
    }, [isGenerating, resultUrl]);
    useEffect(() => {
        if (!revealing) return undefined;
        const id = setTimeout(() => setRevealing(false), 750);
        return () => clearTimeout(id);
    }, [revealing]);

    const showAdjust = !!resultUrl && showResult && !isGenerating && !!onEditsChange;
    const [adjustOpen, setAdjustOpen] = useState(false);
    const toggleEditor = () => setAdjustOpen((o) => { if (!o) onEditorOpen?.(); return !o; });
    const resultShown = editedUrl || resultUrl; // the result as it will be downloaded / printed
    // The icon-only action column (between `sm` and `xl`) — its buttons show their label as a tooltip.
    const compactActions = useMediaQuery('(max-width: 1279px)');
    const tipSide = useMediaQuery('(max-width: 639px)') ? 'top' : 'left'; // the phone row's tooltips go above
    const glass = 'pointer-events-auto bg-white/90 shadow-md backdrop-blur dark:bg-zinc-900/90';
    // The view switch and the size chip stay out of the way until the pointer is over the photo box (or one of them has keyboard focus):
    // they slide in from the left / right edge. The size chip does this at every width and also on the empty upload box (a tap counts
    // as hover on a touch screen); the view switch only exists with a photo, and hides only from `lg` — below that it stays visible.
    const revealCls = (from) => (from === 'left'
        ? cn(
            'transition-[transform,opacity] duration-300 ease-out lg:-translate-x-3 lg:pointer-events-none lg:opacity-0',
            'lg:group-hover/stage:pointer-events-auto lg:group-hover/stage:translate-x-0 lg:group-hover/stage:opacity-100',
            'lg:group-focus-within/stage:pointer-events-auto lg:group-focus-within/stage:translate-x-0 lg:group-focus-within/stage:opacity-100',
        )
        : cn(
            'translate-x-3 pointer-events-none opacity-0 transition-[transform,opacity] duration-300 ease-out',
            'group-hover/stage:pointer-events-auto group-hover/stage:translate-x-0 group-hover/stage:opacity-100',
            'group-focus-within/stage:pointer-events-auto group-focus-within/stage:translate-x-0 group-focus-within/stage:opacity-100',
        ));

    // The floating view switch / size chip / remove button. Below `lg` they sit inside the photo frame, tucked into its top corners;
    // from `lg` they float over the stage as before. All hidden while generating: only the overlay shows then.
    const barInFrame = useMediaQuery('(max-width: 1023px)');
    const topBar = !isGenerating && (
        <div
            className={cn('pointer-events-none absolute z-20 flex flex-wrap items-start gap-2 p-2', barInFrame ? 'inset-x-0 top-0' : 'inset-x-3 top-3')}
            style={!barInFrame && sideGap ? { left: sideGap + 12, right: sideGap + 12 } : undefined}
        >
            {resultUrl && (
                <div className={cn('inline-flex rounded-lg p-0.5', glass, revealCls('left'))} role="group">
                    {[
                        { key: 'original', label: t('studioEditor.original') },
                        { key: 'compare', label: t('studioEditor.compare') },
                        { key: 'result', label: t('studioEditor.result') },
                    ].map((x) => (
                        <button
                            key={x.key}
                            type="button"
                            aria-pressed={view === x.key}
                            onClick={() => pickView(x.key)}
                            className={cn(
                                'rounded-md px-3 py-1 text-xs font-semibold transition-colors',
                                view === x.key ? 'bg-primary text-white shadow-sm' : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300',
                            )}
                        >
                            {x.label}
                        </button>
                    ))}
                </div>
            )}
            <div className="ml-auto flex items-center gap-1.5">
                {/* The size opens the size popup (same one as the panel's size card): a chip until there is a result, then an icon button
                    beside the view button, with the size in its tooltip. */}
                {resultUrl ? (
                    <>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" onClick={onSizeClick} disabled={!onSizeClick} aria-label={sizeLabel(size, num)} className={cn('flex h-8 w-8 items-center justify-center rounded-[10px] text-zinc-700 transition-colors hover:text-primary disabled:pointer-events-none dark:text-zinc-200', glass)}>
                                    <IconRuler2 size={16} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="z-[2000]">{sizeLabel(size, num)}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" onClick={onPreview} aria-label={t('imageGeneration.previewTooltip')} className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-[10px] bg-primary text-white shadow-md transition-colors hover:bg-primary/90">
                                    <IconEye size={17} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.previewTooltip')}</TooltipContent>
                        </Tooltip>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={onSizeClick}
                        disabled={!onSizeClick}
                        className={cn('inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-xs font-medium text-zinc-700 transition-colors hover:text-primary disabled:pointer-events-none dark:text-zinc-200', glass, revealCls('right'))}
                    >
                        <IconRuler2 size={13} />
                        {sizeLabel(size, num)}
                    </button>
                )}
                {/* Remove the photo */}
                {hasPhoto && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={onRemove}
                                aria-label={t('studioEditor.remove')}
                                className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-[10px] bg-red-500 text-white shadow-md transition-colors hover:bg-red-600"
                            >
                                <IconX size={17} strokeWidth={2.5} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="z-[2000]">{t('studioEditor.remove')}</TooltipContent>
                    </Tooltip>
                )}
            </div>
        </div>
    );

    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900 sm:flex sm:items-stretch sm:gap-2 lg:p-3 xl:gap-3">
            <div className={cn('min-w-0 rounded-xl p-2 sm:flex-1', styles.darkPanel)}>
                {/* The stage: the frame (the shape of the print) with the view switch, size chip and remove button floating on top of it */}
                <div ref={stageRef} className="group/stage relative flex justify-center">
                    {!barInFrame && topBar}

                    <div
                        ref={frameRef}
                        className={cn(
                            // Sized so the photo AND its action row fit the first screen; below `lg` the frame stops at 600px tall.
                            'group relative overflow-hidden rounded-lg border-2 [--studio-frame-max:600px] lg:[--studio-frame-max:100000px]',
                            // the tallest the frame may be, so everything fits one screen: on a phone the header, the action row under the
                            // photo, the generate bar and the bottom nav; from `sm` the actions sit beside it; from `lg` the bottom nav is gone;
                            // from `xl` the generate bar moves into the left panel too
                            '[--studio-frame-h:calc(100dvh_-_22rem)] sm:[--studio-frame-h:calc(100dvh_-_19rem)] lg:[--studio-frame-h:calc(100vh_-_16rem)] xl:[--studio-frame-h:calc(100vh_-_11rem)]',
                            hasPhoto ? 'border-transparent bg-white/5' : 'border-dashed border-white/25 bg-white/5',
                            isDragging && 'border-primary bg-primary/5',
                        )}
                        style={{ aspectRatio: `${dims.w} / ${dims.h}`, width: `min(100%, calc(min(var(--studio-frame-h), var(--studio-frame-max)) * ${ratio}))`, minWidth: 220 }}
                        {...dragHandlers}
                    >
                        {hasPhoto ? (
                            view === 'compare' && !isGenerating ? (
                                <CompareSlider fit="contain" position="center bottom" before={previewUrl} after={resultShown} beforeLabel={t('studioEditor.before')} afterLabel={t('studioEditor.after')} />
                            ) : (
                                <div className={cn('absolute inset-0 touch-none', zoom > 1 && 'cursor-grab active:cursor-grabbing', revealing && view === 'result' && styles.reveal)} {...panHandlers}>
                                    <img
                                        src={shownUrl}
                                        alt=""
                                        draggable="false"
                                        className={cn('absolute inset-0 h-full w-full select-none object-contain will-change-transform', !panning && 'transition-transform duration-150')}
                                        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
                                    />
                                </div>
                            )
                        ) : (
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center"
                            >
                                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white"><IconPhotoUp size={28} /></span>
                                <span className="text-base font-semibold !text-white">{t('imageGeneration.uploadInstruction')}</span>
                                <span className="max-w-[260px] text-xs !text-white/70">{t('studioEditor.uploadHint', { size: sizeLabel(size, num) })}</span>
                                <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"><IconUpload size={16} />{t('imageGeneration.uploadButton')}</span>
                                <span className="flex items-center gap-1.5 text-[11px] !text-white/60">
                                    <kbd className="rounded border border-white/25 px-1.5 py-0.5">Ctrl+U</kbd>
                                    {t('studioEditor.orPaste')}
                                    <kbd className="rounded border border-white/25 px-1.5 py-0.5">Ctrl+V</kbd>
                                </span>
                            </button>
                        )}

                        {/* Zoom bar — rises from the bottom edge while the pointer is over the photo (or a button in it has focus) */}
                        {canZoom && !(showAdjust && adjustOpen) && (
                            <div className={cn('pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 translate-y-[calc(100%+0.75rem)] items-center gap-0.5 rounded-full bg-black/60 px-1 py-1 text-white opacity-0 shadow-lg backdrop-blur transition-[transform,opacity] duration-300 ease-out group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 focus-within:pointer-events-auto focus-within:translate-y-0 focus-within:opacity-100')}>
                                <button type="button" onClick={() => setZoomTo(zoom - ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} aria-label={t('studioEditor.zoomOut')} className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-40"><IconMinus size={15} /></button>
                                <span className="min-w-[44px] text-center text-xs font-semibold tabular-nums !text-white">{num(Math.round(zoom * 100))}%</span>
                                <button type="button" onClick={() => setZoomTo(zoom + ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} aria-label={t('studioEditor.zoomIn')} className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-40"><IconPlus size={15} /></button>
                                <button type="button" onClick={() => setZoomTo(1)} disabled={zoom === 1} aria-label={t('studioEditor.zoomReset')} className="flex h-7 w-7 items-center justify-center rounded-full text-amber-300 hover:bg-white/15 disabled:opacity-40"><IconZoomReset size={15} /></button>
                            </div>
                        )}

                        {barInFrame && topBar}

                        {/* Like / dislike — the AI photo editor's pills, above the zoom bar: rise from the bottom edge with it on hover from `lg`,
                            always shown below it (no hover on a touch screen). Only on the result view, not while the edit drawer is open; gone once rated. */}
                        {view === 'result' && !isGenerating && !rated && !adjustOpen && onLike && (
                            <div className="absolute inset-x-0 bottom-12 z-[12] flex items-center justify-center gap-2 transition-[transform,opacity] duration-300 ease-out lg:pointer-events-none lg:translate-y-[calc(100%+3rem)] lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:translate-y-0 lg:group-hover:opacity-100 lg:focus-within:pointer-events-auto lg:focus-within:translate-y-0 lg:focus-within:opacity-100">
                                <button
                                    type="button"
                                    onClick={onLike}
                                    disabled={!!feedbackLoading}
                                    className="inline-flex items-center gap-1.5 rounded-[10px] bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {feedbackLoading === 'positive' ? <IconLoader2 size={14} className="animate-spin" /> : <IconThumbUp size={14} />}
                                    {t('worldCupJersey.feedback.likeLabel')}
                                </button>
                                <button
                                    type="button"
                                    onClick={onDislike}
                                    disabled={!!feedbackLoading}
                                    className="inline-flex items-center gap-1.5 rounded-[10px] bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {feedbackLoading === 'negative' ? <IconLoader2 size={14} className="animate-spin" /> : <IconThumbDown size={14} />}
                                    {t('worldCupJersey.feedback.dislikeLabel')}
                                </button>
                            </div>
                        )}

                        {/* The result's edit drawer — docked to the bottom edge, closed by default */}
                        {showAdjust && (
                            <PhotoEditDrawer
                                value={edits}
                                onChange={onEditsChange}
                                open={adjustOpen}
                                onClose={() => setAdjustOpen(false)}
                                thumbs={filterThumbs}
                                cropped={editCropped}
                                onCrop={onEditCrop}
                                onClearCrop={onClearEditCrop}
                            />
                        )}

                        {isGenerating && (
                            <GeneratingOverlay
                                photoUrl={previewUrl}
                                steps={GEN_STEPS.map((k) => t(`studioEditor.${k}`))}
                                stepDelays={GEN_STEP_DELAYS}
                                onHide={onHide}
                            />
                        )}
                    </div>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    className="hidden"
                    onChange={(e) => {
                        // Snapshot the list BEFORE clearing the input (clearing empties the FileList).
                        const files = Array.from(e.target.files || []);
                        e.target.value = '';
                        if (files[0]) onPickFile(files[0]);
                    }}
                />
            </div>

            {/* Actions on the photo / the result — a compact icon-only strip like the left rail (tooltips) below `xl`: a row under the photo
                on a phone, a column beside it from `sm`; from `xl` a labelled column as wide as its longest button */}
            {hasPhoto && !isGenerating && (
                <div className="mx-auto mt-2 flex w-fit items-center gap-1 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-950 sm:mx-0 sm:mt-0 sm:shrink-0 sm:flex-col sm:self-start xl:w-auto xl:items-stretch xl:justify-start xl:gap-2 xl:self-auto xl:rounded-none xl:border-l xl:border-zinc-100 xl:bg-transparent xl:p-0 xl:pl-3 dark:xl:border-zinc-800 dark:xl:bg-transparent">
                    {resultUrl && showResult ? (
                        <>
                            <ActionBtn compact={compactActions} tipSide={tipSide} icon={customSizeIcon} label={t('imageGeneration.customSizeDownload')} onClick={onCustomDownload} />
                            <ActionBtn compact={compactActions} tipSide={tipSide} icon={btnIcon('/images/photo-tools/copy.svg')} label={t('imageGeneration.copyPhoto')} tooltip={t('imageGeneration.copyTooltip')} onClick={onCopy} loading={copying} />
                            {onEditsChange && <EditToggle compact={compactActions} tipSide={tipSide} open={adjustOpen} edited={isEdited(edits) || editCropped} onClick={toggleEditor} />}
                            {/* download sits right above print — the two ways the finished photo leaves */}
                            <ActionBtn compact={compactActions} tipSide={tipSide} icon={<IconDownload size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />} label={t('imageGeneration.downloadPhoto')} tooltip={t('imageGeneration.downloadTooltip')} onClick={onDownload} />
                            <PrintButton compact={compactActions} tipSide={tipSide} label={t('imageGeneration.printThisImage')} onClick={onPrint} />
                        </>
                    ) : (
                        <>
                            <ActionBtn compact={compactActions} tipSide={tipSide} icon={btnIcon('/images/photo-tools/crop.svg')} label={t('imageGeneration.cropImageBtn')} onClick={onRecrop} />
                            <ActionBtn compact={compactActions} tipSide={tipSide} icon={<IconUpload size={18} className="text-zinc-600 dark:text-zinc-300" />} label={t('studioEditor.changePhoto')} onClick={() => inputRef.current?.click()} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/** The strip of recent studio photos [{ id, aiImage, fileName }] — its own card, so the page can place it under the controls on a phone. */
export function StudioRecentPhotos({ history, historyLoading, onOpenHistory, bgJobs = [], onOpenJob, onDismissJob }) {
    const { t } = useTranslation();
    return (
        <RecentPhotosStrip
            title={t('studioEditor.recentTitle')}
            emptyText={t('studioEditor.recentEmpty')}
            history={history}
            historyLoading={historyLoading}
            onOpenHistory={onOpenHistory}
            bgJobs={bgJobs}
            onOpenJob={onOpenJob}
            onDismissJob={onDismissJob}
            viewAllTo="/documents?tab=images&sub=studio"
        />
    );
}
